import {
  Navigate,
  RouterProvider,
  createBrowserRouter,
  useNavigate,
} from 'react-router-dom';
import { GlobalError } from '@antimatterhq/ui/components/globalError';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Text } from '@antimatterhq/ui/components/typography';
import { FlagSet } from '@antimatterhq/ui/utils/flags';
import Root from '@/pages';
import Home from '@/pages/home';
import Configure from '@/pages/configure';
import DataSources from '@/pages/configure/data-sources';
import Transforms from '@/pages/configure/transforms';
import DetectionRules from '@/pages/configure/detection-rules';
import Observables from '@/pages/explore/observables';
import AddDataSourceStep from '@/pages/configure/data-sources/add/step';
import EditDataSource from '@/pages/configure/data-sources/edit';
import Explore from '@/pages/explore';
import Notables from '@/pages/explore/notables';
import Health from '@/pages/explore/health';
import WorkspacePage from '@/pages/configure/workspace';
import Observable from '@/pages/explore/observables/:kind';
import DetectionRulesAddPage from '@/pages/configure/detection-rules/add';
import { NotableDetails } from '@/pages/explore/notables/details';
import DetectionRulesEditPage from '@/pages/configure/detection-rules/edit/:name';
import DetectionRulesReviewPage from '@/pages/configure/detection-rules/review';
import { ViewHealthDetails } from '@/pages/explore/health/view';
import Admin from '@/pages/configure/admin';
import ObservableEvents from '@/pages/explore/observables/:kind/:value/events';
import Initialization from '@/pages/initialization';
import { useConfig } from '@/contexts/ConfigContext';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { useParentDarkModeSync } from '@/hooks/iframe/useParentDarkModeSync';
import AddTransform from '@/pages/configure/transforms/add';
import EditTransform from '@/pages/configure/transforms/edit/:name';
import QueryPage from '@/pages/explore/query';
import DatasourceUpgrade from '@/pages/configure/data-sources/upgrade';
import AddDataSource from '@/pages/configure/data-sources/add';

const FourOhFour = () => {
  const navigate = useNavigate();
  useParentDarkModeSync();

  return (
    <GlobalError
      title="Page not found"
      description="The page you are looking for does not exist."
      className="h-screen w-screen"
      onRetry={async () => {
        navigate('/home');
      }}
      retryText="Go to home page"
    />
  );
};

const EnvironmentInfo = () => {
  const { instanceId } = useInstanceId();
  const flags = useFlags<FlagSet>();
  const config = useConfig();
  useParentDarkModeSync();

  return (
    <Text>
      <pre>
        {JSON.stringify(
          {
            version: import.meta.env.VITE_VERSION,
            instanceId,
            config,
            flags,
          },
          null,
          2,
        )}
      </pre>
    </Text>
  );
};

const router = createBrowserRouter(
  [
    {
      path: '/',
      element: <Root />,
      errorElement: <FourOhFour />,
      children: [
        {
          path: '',
          element: <Navigate to="home" replace />,
        },
        {
          path: 'home',
          element: <Home />,
        },
        {
          path: 'initialization',
          element: <Initialization />,
        },
        {
          path: 'explore',
          element: <Explore />,
          children: [
            {
              path: '',
              element: <Navigate to="notables" replace />,
            },
            {
              path: 'notables',
              element: <Notables />,
            },
            {
              path: 'notables/:id',
              element: <NotableDetails />,
            },
            {
              path: 'observables',
              element: <Observables />,
            },
            {
              path: 'observables/:kind',
              element: <Observable />,
            },
            {
              path: 'observables/:kind/:value/events',
              element: <ObservableEvents />,
            },
            {
              path: 'health',
              element: <Health />,
            },
            {
              path: 'health/view/:id',
              element: <ViewHealthDetails />,
            },
            {
              path: 'query',
              element: <QueryPage />,
            },
          ],
        },
        {
          path: 'configure',
          element: <Configure />,
          children: [
            {
              path: '',
              element: <Navigate to="data-sources" replace />,
            },
            {
              path: 'data-sources',
              element: <DataSources />,
            },
            {
              path: 'data-sources/add',
              children: [
                {
                  path: '',
                  element: <AddDataSource />,
                },
                {
                  path: 'source',
                  element: <AddDataSourceStep />,
                },
                {
                  path: 'getting-started',
                  element: <AddDataSourceStep />,
                },
              ],
            },
            {
              path: 'data-sources/edit/:name',
              children: [
                {
                  path: '',
                  element: <Navigate to="source" replace />,
                },
                {
                  path: 'source',
                  element: <EditDataSource />,
                },
                {
                  path: 'getting-started',
                  element: <EditDataSource />,
                },
                {
                  path: 'bronze',
                  element: <EditDataSource />,
                },
                {
                  path: 'silver-pre-transform',
                  element: <EditDataSource />,
                },
                {
                  path: 'silver',
                  element: <EditDataSource />,
                },
                {
                  path: 'gold',
                  element: <EditDataSource />,
                },
                {
                  path: 'review',
                  element: <EditDataSource />,
                },
              ],
            },
            {
              path: 'data-sources/upgrade/:name',
              element: <DatasourceUpgrade />,
            },
            {
              path: 'transforms',
              element: <Transforms />,
            },
            {
              path: 'transforms/add',
              element: <AddTransform />,
            },
            {
              path: 'transforms/edit/:name',
              element: <EditTransform />,
            },
            {
              path: 'detection-rules',
              element: <DetectionRules />,
            },
            {
              path: 'detection-rules/add',
              element: <DetectionRulesAddPage />,
            },
            {
              path: 'detection-rules/edit/:name',
              element: <DetectionRulesEditPage />,
            },
            {
              path: 'detection-rules/review/:name',
              element: <DetectionRulesReviewPage />,
            },
            {
              path: 'workspace',
              element: <WorkspacePage />,
            },
            {
              path: 'admin',
              element: <Admin />,
            },
          ],
        },
      ],
    },
    {
      path: '/_environment',
      element: <EnvironmentInfo />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

export const AppRouterProvider = () => {
  return (
    <RouterProvider router={router} future={{ v7_startTransition: true }} />
  );
};
